import React, { useState } from "react";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  TextField,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import isEqual from "lodash/isEqual";

import { useWarningSnackbar } from "../../../Components/SharedSnackbar/SharedSnackbar.context";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { isCustomerInPresentationMode } from "../../../Context/useCustomerOrPresentationModeCustomer";
import TagsSelector from "../Common/TagsSelector";
import { PRIORITY_LIST } from "../constants";
import { type InsightPriority } from "../types";

const MAX_NOTES_LENGTH = 1000;

export type InsightEnhancementData = {
  priority: InsightPriority | "";
  tags: string[];
  expertNote: string;
};

type Props = {
  existingPriority: string;
  existingTags: string[];
  existingExpertNote: string;
  open: boolean;
  onClose: () => void;
  onSave: (enhancementData: InsightEnhancementData) => void;
};

const EnhanceInsightModal: React.FC<Props> = ({
  existingPriority,
  existingTags,
  existingExpertNote,
  open,
  onClose,
  onSave,
}) => {
  const { customer } = useCustomerContext();
  const isPresentationMode = isCustomerInPresentationMode(customer);

  const warningSnackbar = useWarningSnackbar();

  const [priority, setPriority] = useState<InsightPriority | "">((existingPriority as InsightPriority) || "");
  const [tags, setTags] = useState<string[]>(existingTags);
  const [notes, setNotes] = useState(existingExpertNote);

  const isSaveEnabled = priority !== existingPriority || !isEqual(tags, existingTags) || notes !== existingExpertNote;

  const resetState = () => {
    setPriority(existingPriority as InsightPriority);
    setTags(existingTags);
    setNotes(existingExpertNote);
  };

  const handleTagsChange = (newTags: string[]) => {
    setTags(newTags);
  };

  const handleExpertNoteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value.length > MAX_NOTES_LENGTH) {
      warningSnackbar(`Expert notes are limited to ${MAX_NOTES_LENGTH} characters.`);
      return;
    }
    setNotes(value);
  };

  const handleSave = () => {
    if (!isSaveEnabled) return;
    const enhancementData = { priority, tags, expertNote: notes };
    onSave(enhancementData);
    onClose();
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>Enhance insight</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Box sx={{ flex: 1, mt: 1.5 }}>
          <TextField
            select
            fullWidth
            label="Priority"
            value={priority}
            data-testid="priority-selector"
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
            onChange={(e) => {
              setPriority(e.target.value as InsightPriority);
            }}
          >
            {PRIORITY_LIST.map((priorityOption) => (
              <MenuItem
                key={priorityOption.value}
                value={priorityOption.value}
                data-testid={`priority-option-${priorityOption.value}`}
              >
                {priorityOption.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <TagsSelector maxTags={3} maxTagChars={24} tags={tags} onTagsChange={handleTagsChange} />
        <Box sx={{ flex: 2 }}>
          <TextField
            multiline
            rows={9}
            fullWidth
            label="Expert notes"
            value={notes}
            onChange={handleExpertNoteChange}
            variant="outlined"
            data-testid="expert-notes-field"
            slotProps={{
              inputLabel: {
                shrink: true,
              },
              input: {
                sx: { pt: 2, pb: 2 },
              },
            }}
          />
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={handleClose} data-testid="cancel-button">
          Cancel
        </Button>
        <Tooltip
          title={isPresentationMode ? "Insights cannot be enhanced in Presentation mode." : ""}
          placement="bottom"
          data-testid="save-button-tooltip"
        >
          <span>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={isPresentationMode || !isSaveEnabled}
              data-testid="save-button"
            >
              Save changes
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default EnhanceInsightModal;
